import React, { useCallback } from 'react';

import { Button } from 'components/base/button';

import { Colors } from 'theme/colors';
import { FontSize } from 'theme/fonts';
import { Container } from 'theme/layout';
import { Regular } from 'theme/text';

import { Action, IActionHandler } from './actions';

const ACTION_WIDTH = 310;
const MOBILE_WIDTH = 150;
const DESKTOP_MARGIN = 10;

/**
 * Renders a list of action buttons.
 *
 * @param props - Action button props.
 */
export function ActionButtons(props: ActionProps) {
  const { actions, actionHandler, loading, accentColor, textColor } = props;

  const renderButton = useCallback(
    (action: Action) => {
      const { title } = action;
      const handler = actionHandler.getAction(action);
      const buttonColor = accentColor ?? Colors.green;
      const buttonTextColor = textColor ?? Colors.white;

      return (
        <Container as="div" width={['100%', MOBILE_WIDTH]} mb={[DESKTOP_MARGIN, 0]} key={title}>
          <Button
            key={title}
            rounded
            accentColor={buttonColor}
            onPress={handler}
            loading={loading}>
            <Regular fontSize={FontSize.Medium} color={buttonTextColor}>
              {title}
            </Regular>
          </Button>
        </Container>
      );
    },
    [loading, actionHandler]
  );

  return (
    <Container
      as="div"
      display="flex"
      flexDirection={['column', 'row']}
      justifyContent="space-between"
      width={['100%', ACTION_WIDTH]}
      mx="auto"
      mt="30px">
      {actions.map(renderButton)}
    </Container>
  );
}
interface ActionProps {
  /** A list of actions available to user. */
  actions: Action<Record<string, unknown>>[];
  /** An action handler for processing user actions. */
  actionHandler: IActionHandler;
  /** If true, shows loading indicator. */
  loading: boolean;
  /** CoBranding accent color to be used in buttons and potentially other controls */
  accentColor: string;
  /** CoBranding text color to be used in buttons and potentially other controls */
  textColor: string;
}
